<template>
  <div class="docs-app">
    <header class="app-header">
      <div class="brand-group">
        <button class="menu-toggle" type="button" aria-label="Open navigation" @click="mobileNavigationOpen = true">
          <span></span><span></span><span></span>
        </button>
        <img class="brand-logo" :src="isDark ? '/iosea-logo-white-cropped.png' : '/iosea-logo-cropped.png'" alt="IOSEA">
        <span class="brand-label">Docs</span>
      </div>

      <div class="header-controls">
        <ThemeSwitch />

        <details v-if="user" ref="accountMenu" class="account-menu">
          <summary aria-label="Open account menu">
            <span class="account-avatar">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="8" r="4"></circle>
                <path d="M4.5 21c.7-4.2 3.3-6.3 7.5-6.3s6.8 2.1 7.5 6.3"></path>
              </svg>
            </span>
          </summary>
          <div class="account-dropdown">
            <div class="account-email">{{ user.email }}</div>
            <button type="button" @click="onClickLogout">Logout</button>
          </div>
        </details>
      </div>
    </header>

    <div class="docs-shell">
      <aside class="sidebar desktop-navigation">
        <DocsNavigation
          v-model:search="navigationSearch"
          :groups="filteredNavigationGroups"
          search-id="desktop-docs-search"
          :selected-key="sectionNumber"
          @select="selectPage"
        />
      </aside>

      <div v-if="mobileNavigationOpen" class="navigation-backdrop" @click="mobileNavigationOpen = false"></div>
      <aside class="sidebar mobile-navigation" :class="{ open: mobileNavigationOpen }">
        <div class="mobile-navigation-heading">
          <strong>Documentation</strong>
          <button type="button" aria-label="Close navigation" @click="mobileNavigationOpen = false">Close</button>
        </div>
        <DocsNavigation
          v-model:search="navigationSearch"
          :groups="filteredNavigationGroups"
          search-id="mobile-docs-search"
          :selected-key="sectionNumber"
          @select="selectPage"
        />
      </aside>

      <main ref="mainContent" class="docs-content" tabindex="-1">
        <template v-if="selectedPage">
          <div class="page-toolbar"><p class="breadcrumb">{{ selectedPage.groupLabel }} / {{ selectedPage.title }}</p></div>
          <article class="guide-content"><component :is="selectedPage.component" /></article>
        </template>
        <p v-else>No documentation pages have been added yet.</p>
      </main>
    </div>

    <footer class="app-footer">
      <div>Copyright &copy; 2026 IOSEA Inc. All rights reserved.</div>
      <div class="footer-version">Documentation version 10-15-2026</div>
    </footer>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import DocsNavigation from '../components/DocsNavigation.vue';
import ThemeSwitch from '../components/ThemeSwitch.vue';
import useTheme from '../composables/useTheme';
import getUser from '../composables/getUser';
import useLogout from '../composables/useLogout';
import documentation from '../config/documentation';

const pages = documentation.flatMap(group => group.pages.map(page => ({
  ...page,
  groupLabel: group.label
})));

export default {
  name: 'Home',
  components: { DocsNavigation, ThemeSwitch },
  setup() {
    const router = useRouter();
    const { user } = getUser();
    const { logout, error } = useLogout();
    const { isDark } = useTheme();
    const sectionNumber = ref(pages[0]?.key || '');
    const navigationSearch = ref('');
    const mobileNavigationOpen = ref(false);
    const mainContent = ref(null);
    const accountMenu = ref(null);
    const selectedPage = computed(() => pages.find(page => page.key === sectionNumber.value));
    const filteredNavigationGroups = computed(() => {
      const search = navigationSearch.value.trim().toLowerCase();
      return documentation.map(group => ({
        id: group.id,
        label: group.label,
        categories: [{
          id: group.id,
          entries: group.pages.filter(page =>
            group.label.toLowerCase().includes(search) || page.title.toLowerCase().includes(search)
          )
        }]
      })).filter(group => group.categories[0].entries.length);
    });
    const selectPage = (key) => {
      sectionNumber.value = key;
      mobileNavigationOpen.value = false;
      mainContent.value?.focus();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    const onClickLogout = async () => {
      await logout();
      if (!error.value) router.push({ name: 'SignIn' });
    };
    const closeAccountMenu = (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'keydown' || !accountMenu.value?.contains(event.target)) accountMenu.value?.removeAttribute('open');
      if (event.type === 'keydown') mobileNavigationOpen.value = false;
    };
    onMounted(() => {
      document.addEventListener('click', closeAccountMenu);
      document.addEventListener('keydown', closeAccountMenu);
    });
    onBeforeUnmount(() => {
      document.removeEventListener('click', closeAccountMenu);
      document.removeEventListener('keydown', closeAccountMenu);
    });
    watch(user, () => { if (!user.value) router.push({ name: 'SignIn' }); });
    return { user, isDark, sectionNumber, navigationSearch, mobileNavigationOpen, mainContent, accountMenu, selectedPage, filteredNavigationGroups, selectPage, onClickLogout };
  }
};
</script>
